import Button, { ButtonProps } from '@carvana/ds-button';
import { AnalyticsAttributes } from '@carvana/merch-components/contracts';
import cx from 'classnames';
import React, { useCallback } from 'react';
import PrimaryCtaType from '~/constants/PrimaryCtaType';
import SecondaryCtaType from '~/constants/SecondaryCtaType';
import { useConfiguration } from '~/context/common/StaticContext';
import useAccountModalReady from '~/hooks/common/accountModal/useAccountModalReady';
import { usePrimaryButtonHandler } from '~/hooks/vdp/cta';
import usePrimaryCtaButtonState from '~/hooks/vdp/selectors/usePrimaryCtaButtonState';
import useSecondaryCtaButtonState from '~/hooks/vdp/selectors/useSecondaryCtaButtonState';

const dummyFunction = () => {};

// Types that open the account modal for logged-out users and require it to be ready.
const MODAL_CTA_TYPES = new Set([
  PrimaryCtaType.GetStarted,
  PrimaryCtaType.Reservable
]);

type OwnProps<T> = {
  ButtonComponent?: React.ComponentType<any>;
  className?: string;
  'data-analytics-id': string | null;
  'data-analytics-attributes'?: AnalyticsAttributes;
  'data-testid'?: string;
  getStartedText?: string;
  onClick?: () => void;
} & T;

export default function PrimaryCtaButton<T = ButtonProps>({
  ButtonComponent = Button,
  className = '',
  'data-analytics-id': analyticsId,
  'data-analytics-attributes': analyticsAttributes,
  'data-testid': testId,
  getStartedText,
  onClick = dummyFunction,
  ...rest
}: OwnProps<T>) {
  const { shouldUseTitleCasing } = useConfiguration();
  const defaultGetStartedText = shouldUseTitleCasing
    ? 'Get Started'
    : 'Get started';
  const resolvedGetStartedText = getStartedText ?? defaultGetStartedText;
  const { whenReady, isPending: isPendingModal } = useAccountModalReady();
  const {
    isDisabled,
    isLoading: isAvailabilityLoading,
    type
  } = usePrimaryCtaButtonState();
  const { type: secondaryType } = useSecondaryCtaButtonState();
  const rawClickHandler = usePrimaryButtonHandler(onClick) ?? dummyFunction;

  const handleOnClick = useCallback(() => {
    if (MODAL_CTA_TYPES.has(type)) {
      whenReady(rawClickHandler);
    } else {
      rawClickHandler();
    }
  }, [whenReady, rawClickHandler, type]);

  const isLoading = isAvailabilityLoading || isPendingModal;

  const primaryCtaTypes: Record<PrimaryCtaType, string | null> = {
    [PrimaryCtaType.CancelPreOrder]: null,
    [PrimaryCtaType.ContinuePurchase]: shouldUseTitleCasing
      ? 'Continue Purchase'
      : 'Continue purchase',
    [PrimaryCtaType.Default]: resolvedGetStartedText,
    [PrimaryCtaType.DisabledGetStarted]: resolvedGetStartedText,
    [PrimaryCtaType.DisabledPreOrder]: shouldUseTitleCasing
      ? 'Pre-Order Now'
      : 'Pre-order now',
    [PrimaryCtaType.GetAlerts]: shouldUseTitleCasing
      ? 'Get Alerts'
      : 'Get alerts',
    [PrimaryCtaType.GetStarted]: resolvedGetStartedText,
    [PrimaryCtaType.NotifyMe]: shouldUseTitleCasing ? 'Notify Me' : 'Notify me',
    [PrimaryCtaType.Reservable]: shouldUseTitleCasing
      ? 'Pre-Order Now'
      : 'Pre-order now',
    [PrimaryCtaType.Reservation]: null,
    [PrimaryCtaType.ViewSimilarVehicles]:
      secondaryType === SecondaryCtaType.Default
        ? shouldUseTitleCasing
          ? 'View Similar Vehicles'
          : 'View similar vehicles'
        : shouldUseTitleCasing
        ? 'View Similar'
        : 'View similar'
  };

  return primaryCtaTypes[type] ? (
    <ButtonComponent
      className={cx(className, {
        '!bg-[#00874a] hover:!bg-[var(--reference-green-600)] active:!bg-[#0a4729]':
          !isDisabled
      })}
      disabled={isDisabled}
      data-adobe-id={null}
      data-analytics-id={analyticsId}
      data-analytics-attributes={JSON.stringify({
        ...analyticsAttributes,
        description: 'primary cta',
        text: primaryCtaTypes[type]
      })}
      data-testid={testId}
      isLoading={isLoading}
      onClick={handleOnClick}
      variant="primary"
      {...rest}
    >
      {primaryCtaTypes[type]}
    </ButtonComponent>
  ) : null;
}
